import { forwardRef, useId, type InputHTMLAttributes, type ReactNode } from 'react';
import { clsx } from 'clsx';

interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  label?: string;
  error?: string;
  hint?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  { label, error, hint, leftIcon, rightIcon, className, id, ...rest },
  ref
) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const describedById = error || hint ? `${inputId}-desc` : undefined;
  const hasError = Boolean(error);

  return (
    <div className="flex flex-col gap-1.5">
      {label && (
        <label
          htmlFor={inputId}
          className="text-xs font-medium text-ink-secondary uppercase tracking-wider"
        >
          {label}
        </label>
      )}
      <div className="relative flex items-center">
        {leftIcon && (
          <span className="absolute left-3 text-ink-tertiary pointer-events-none">{leftIcon}</span>
        )}
        <input
          ref={ref}
          id={inputId}
          aria-invalid={hasError || undefined}
          aria-describedby={describedById}
          className={clsx(
            'h-9 w-full rounded-md border bg-surface text-sm text-ink-primary placeholder:text-ink-tertiary',
            'transition-colors focus-visible:outline-none focus-visible:ring-2',
            hasError
              ? 'border-danger focus-visible:ring-danger'
              : 'border-border focus-visible:ring-accent focus-visible:border-accent',
            leftIcon ? 'pl-9' : 'pl-3',
            rightIcon ? 'pr-9' : 'pr-3',
            className
          )}
          {...rest}
        />
        {rightIcon && (
          <span className="absolute right-3 text-ink-tertiary pointer-events-none">
            {rightIcon}
          </span>
        )}
      </div>
      {error ? (
        <span id={describedById} className="text-xs text-danger">
          {error}
        </span>
      ) : hint ? (
        <span id={describedById} className="text-xs text-ink-tertiary">
          {hint}
        </span>
      ) : null}
    </div>
  );
});
